import { useState, useEffect, useLayoutEffect } from 'react';
import { Capacitor } from '@capacitor/core';

/**
 * useMobile — production-grade mobile detection for LensVerse
 *
 * Strategy (in priority order):
 * 1. Capacitor native platform → always mobile
 * 2. ResizeObserver on document.documentElement (viewport-accurate, works in WebView)
 * 3. window.innerWidth fallback (resize event)
 * 4. SSR-safe: returns false during SSR
 *
 * Threshold: 1024px (lg breakpoint), matching Tailwind config.
 *
 * CRITICAL FIX — why the old hook failed on Capacitor:
 *   • On Android WebView, window.innerWidth is unreliable during the first
 *     render because the WebView reports 0 or the unscaled device width before
 *     the viewport meta tag is processed.
 *   • ResizeObserver fires *after* layout, so it always sees the true CSS pixel
 *     viewport width.
 *   • Capacitor.isNativePlatform() is synchronous and definitive — on Android
 *     we are *always* mobile regardless of window size (split-screen, etc.).
 */

const MOBILE_BREAKPOINT = 1024;

// useLayoutEffect on client, useEffect on server (avoids SSR warning)
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

function getInitialMobile() {
  // Always mobile inside Capacitor WebView
  if (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform()) {
    return true;
  }
  // SSR / no window
  if (typeof window === 'undefined') return false;
  return window.innerWidth < MOBILE_BREAKPOINT;
}

export default function useMobile() {
  const [isMobile, setIsMobile] = useState(getInitialMobile);

  useIsomorphicLayoutEffect(() => {
    // Native platform — unconditionally mobile, no listener needed
    if (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform()) {
      setIsMobile(true);
      return;
    }

    const update = (width) => {
      setIsMobile(width < MOBILE_BREAKPOINT);
    };

    // ResizeObserver is the authoritative source in WebView environments
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          // Use borderBoxSize when available (more accurate than contentRect)
          const width = entry.borderBoxSize
            ? entry.borderBoxSize[0].inlineSize
            : entry.contentRect.width;
          update(width);
        }
      });
      ro.observe(document.documentElement);
      // Fire immediately in case ResizeObserver doesn't fire synchronously
      update(document.documentElement.clientWidth || window.innerWidth);
      return () => ro.disconnect();
    }

    // Fallback: window resize event
    const onResize = () => update(window.innerWidth);
    window.addEventListener('resize', onResize, { passive: true });
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return isMobile;
}

/**
 * usePlatform — granular platform detection
 * Returns: 'android' | 'ios' | 'web'
 */
export function usePlatform() {
  const [platform] = useState(() => {
    if (typeof Capacitor !== 'undefined') {
      return Capacitor.getPlatform(); // 'android' | 'ios' | 'web'
    }
    return 'web';
  });
  return platform;
}